// src/main.js
import Vue from 'vue'
import VueRouter from 'vue-router'
import DashboardPlugin from './material-dashboard'

// Plugins
import App from './App.vue'
import Chartist from 'chartist'
import VueIziToast from 'vue-izitoast'
import GAuth from 'vue-google-oauth2'

// router setup
import routes from './routes/routes'

// store setup
import store from './store'

// methods
import { http, requestOnAPI, login, setAPIToken, setInterceptors } from './methods/api/requests'
import { saveSession, getSession, saveSameAsStore, clearStorage } from './methods/storage'
import { fromArrayBuffer } from './methods/file'

// Configuração do ambiente
const isDevelopment = process.env.NODE_ENV === 'development';
// plugin setup
Vue.use(DashboardPlugin)
Vue.use(VueIziToast, {
  position: 'topRight',
  layout: 2,
  backgroundColor: 'rgba(255, 255, 255, 1)',
})

// Configuração do GAuth baseada no ambiente
const gauthOptions = {
  clientId: isDevelopment
    ? '64314514042-g8gje3rjoe3begj0osl2omrm9uh7ogfo.apps.googleusercontent.com' // stg
    : '725729760329-878m004v74gik0dj9857do9ieeg9q1h0.apps.googleusercontent.com', // prd
  scope: 'email',
  prompt: 'select_account'
};

Vue.use(GAuth, gauthOptions);

// Handler global para erros não capturados
Vue.config.errorHandler = function(err, vm, info) {
  console.error('Vue Error:', err);
  console.error('Component:', vm);
  console.error('Info:', info);
};

window.addEventListener('unhandledrejection', function(event) {
  console.error('Unhandled Promise Rejection:', event.reason);
});

// Provide a nice shortcut to axios within the application
Vue.prototype.$http = http

Vue.prototype.$api = {
  login,
  request: requestOnAPI,
  setToken: setAPIToken,
  setInterceptors: setInterceptors
}

// Modificar para usar async/await
Vue.prototype.$storage = {
  saveSameAsStore: async () => await saveSameAsStore(store),
  getSession: async (keyName) => await getSession(keyName),
  clearStorage: () => clearStorage(store),
}

// Handle file and helper function
Vue.prototype.$file = {
  fromArrayBuffer: fromArrayBuffer,
}

// configure router
const router = new VueRouter({
  routes,
  linkExactActiveClass: 'nav-item active',
  scrollBehavior() {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ x: 0, y: 0 })
      }, 500)
    })
  },
})

async function hasPermissionFor(profiles) {
  if (store.getters['PROFILE'] === '') {
    const profile = await Vue.prototype.$storage.getSession('PROFILE');
    store.commit('PROFILE', profile);
  }
  return profiles.find(profile =>
    profile === store.getters['PROFILE'] ||
    'admin' === store.getters['PROFILE']
  )
}

// Modificar para usar async/await
router.beforeEach(async (to, from, next) => {
  try {
    const isUserLogged = await Vue.prototype.$storage.getSession('ISUSERLOGGED');
    if (isUserLogged === true) {
      if (to.name !== 'Login') {
        const hasPermission = await hasPermissionFor(to.meta.profiles);
        if (hasPermission) {
          next();
        } else {
          if (from.name === 'Login') {
            next({ name: 'Dashboard' });
          } else {
            router.go(-1);
          }
        }
      } else {
        next();
      }
    } else {
      if (to.name !== 'Login') {
        // Salvar a URL atual no localStorage antes do redirecionamento OAuth
        const currentUrl = window.location.href.substr(window.location.origin.length);
        const currentHash = window.location.hash;

        // console.log('Router guard - currentUrl:', currentUrl);
        // console.log('Router guard - currentHash:', currentHash);

        // Se não contém code= (primeira visita), salvar no localStorage
        if (!currentUrl.includes('code=')) {
          const urlToSave = currentHash || currentUrl;
          // console.log('Router guard - Saving to localStorage:', urlToSave);
          localStorage.setItem('pendingRedirect', urlToSave);
          store.commit('REDIRECTO', urlToSave);
        } else {
          // Se contém code= (retorno do OAuth), tentar recuperar do localStorage
          const savedUrl = localStorage.getItem('pendingRedirect');
          if (savedUrl) {
            // console.log('Router guard - Recovered from localStorage:', savedUrl);
            store.commit('REDIRECTO', savedUrl);
          } else {
            // console.log('Router guard - No saved URL found');
            store.commit('REDIRECTO', '/');
          }
        }

        next({ name: 'Login' });
      } else {
        next();
      }
    }
  } catch (error) {
    console.error('Router error:', error);
    next({ name: 'Login' });
  }
});

Vue.use(VueRouter)

// global library setup
Object.defineProperty(Vue.prototype, '$Chartist', {
  get () {
    return this.$root.Chartist
  }
})

const clearSessionData = () => {
  window.sessionStorage.clear();
};

// Adicionar o evento
window.addEventListener('beforeunload', clearSessionData);

// Criar e montar a aplicação de forma assíncrona
async function initializeApp() {
  try {
    const app = new Vue({
      el: '#app',
      render: h => h(App),
      router,
      store,
      data: {
        Chartist: Chartist
      },
      async created() {
        this.$api.setInterceptors.call(this);

        try {
          const isUserLogged = await this.$storage.getSession('ISUSERLOGGED');
          if (isUserLogged === true) {
            this.$store.commit('ISUSERLOGGED', true);
            this.$store.commit('USERNAME', await this.$storage.getSession('USERNAME'));
            this.$store.commit('USERID', await this.$storage.getSession('USERID'));
            this.$store.commit('PROFILE', await this.$storage.getSession('PROFILE'));
            this.$store.commit('CURRENTPROFILE', await this.$storage.getSession('CURRENTPROFILE'));

            const token = await this.$storage.getSession('USERTOKEN');
            if (token) {
              this.$api.setToken(token);
            }
          }
        } catch (error) {
          console.error('Error initializing application:', error);
          this.$storage.clearStorage();
          this.$router.push({ name: 'Login' });
        }
      },
      beforeDestroy() {
        window.removeEventListener('beforeunload', clearSessionData);
      }
    });

    return app;
  } catch (error) {
    console.error('Error creating Vue instance:', error);
    throw error;
  }
}

// Inicializar a aplicação
initializeApp().catch(error => {
  console.error('Failed to initialize application:', error);
});
